import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { apiErrorCode, apiErrorMessage, apiErrorStatus } from '../../services/api-error';
import { EnrollmentService } from '../../services/enrollment.service';
import { PublicOperationService } from '../../services/public-operation.service';
import { TurnstileService } from '../../services/turnstile.service';
import { Dossier } from '../../shared/dossier/dossier';
import { StatePanel } from '../../shared/state-panel/state-panel';

interface EnrollError {
  field: 'codeName' | null;
  message: string;
}

// Inscription publique d'un agent (/operations/:operationId/agents, TD-019).
@Component({
  selector: 'app-enroll-page',
  imports: [Dossier, StatePanel, RouterLink],
  templateUrl: './enroll-page.html',
  styleUrl: './enroll-page.scss',
})
export class EnrollPage {
  readonly operationId = input.required<string>();

  private readonly router = inject(Router);
  private readonly enrollment = inject(EnrollmentService);
  private readonly turnstile = inject(TurnstileService);

  protected readonly operation = inject(PublicOperationService).operation(() => this.operationId());
  protected readonly notFound = computed(() => apiErrorStatus(this.operation.error()) === 404);
  protected readonly loadError = computed(() =>
    this.operation.error() && !this.notFound() ? apiErrorMessage(this.operation.error()) : null,
  );
  // Inscription ouverte seulement pour une operation validee (OPERATION_REGISTRATION_CLOSED sinon).
  protected readonly recruiting = computed(
    () => this.operation.value()?.statusCode === 'VALIDATED',
  );

  protected readonly firstName = signal('');
  protected readonly codeName = signal('');
  protected readonly turnstileToken = signal<string | null>(null);
  protected readonly submitting = signal(false);
  protected readonly error = signal<EnrollError | null>(null);
  protected readonly canSubmit = computed(
    () =>
      !!this.firstName().trim() &&
      !!this.codeName().trim() &&
      !!this.turnstileToken() &&
      !this.submitting(),
  );

  private readonly widget = viewChild<ElementRef<HTMLElement>>('turnstile');
  private removeWidget: (() => void) | null = null;

  constructor() {
    effect(() => {
      const element = this.widget()?.nativeElement;
      if (element) {
        untracked(() => this.renderWidget(element));
      }
    });
    inject(DestroyRef).onDestroy(() => this.removeWidget?.());
  }

  protected async enroll(event: Event): Promise<void> {
    event.preventDefault();
    const turnstileToken = this.turnstileToken();
    if (!this.canSubmit() || !turnstileToken) {
      return;
    }
    this.submitting.set(true);
    this.error.set(null);
    try {
      const result = await this.enrollment.enroll(
        {
          publicId: this.operationId(),
          firstName: this.firstName().trim(),
          codeName: this.codeName().trim(),
        },
        turnstileToken,
      );
      this.router.navigate(['/missions', result.missionId, 'briefing']);
    } catch (error) {
      const field = apiErrorCode(error) === 'AGENT_CODE_NAME_TAKEN' ? 'codeName' : null;
      this.error.set({ field, message: apiErrorMessage(error) });
      // Un jeton Turnstile ne sert qu'une fois : nouvelle verification.
      const element = this.widget()?.nativeElement;
      if (element) {
        this.renderWidget(element);
      }
    } finally {
      this.submitting.set(false);
    }
  }

  private async renderWidget(element: HTMLElement): Promise<void> {
    this.removeWidget?.();
    this.removeWidget = null;
    this.turnstileToken.set(null);
    try {
      this.removeWidget = await this.turnstile.render(element, (token) =>
        this.turnstileToken.set(token),
      );
    } catch (error) {
      this.error.set({
        field: null,
        message: error instanceof Error ? error.message : apiErrorMessage(error),
      });
    }
  }
}
